import { useState } from "react";
import { diaAr } from "../../shared/fechaAr";
import Patente from "./Patente";
import { consultarMultasPersona } from "../../services/multasPersona";

/* Multas de tránsito buscadas por DNI (no por dominio) -- para la ficha de
 * una persona, no de una unidad.
 *
 * ── NO HAY DATO GUARDADO, Y ES A PROPÓSITO ───────────────────────────────
 *
 * `BloqueMultas` (la de `Unidad.jsx`) muestra lo último consultado ANTES de
 * pedir nada, porque el dato queda anotado por dominio. Acá no: una
 * consulta por DNI puede traer actas de vehículos distintos, y CABA ni
 * siquiera expone de qué dominio es cada una en este modo -- no hay una
 * clave limpia para guardarlo bajo `multas`, que es por dominio. Así que
 * este bloque no muestra nada hasta que se aprieta el botón, y lo que
 * trae se pierde al salir de la pantalla.
 *
 * ── EL SEXO NO ES UN DATO QUE TENGAMOS, Y HAY QUE PEDIRLO ────────────────
 *
 * PBA y SINAI lo exigen en su propio formulario para buscar por documento
 * (no es una decisión de acá) y no hay ningún campo de sexo en el padrón
 * de personas. Por eso es una elección en el momento de consultar, no un
 * dato precargado: sin elegirlo, sólo se consulta CABA.
 */

const money = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return "$" + new Intl.NumberFormat("es-AR", { maximumFractionDigits: 0 }).format(v);
};

const BloqueMultasPersona = ({ dni, puedeConsultar }) => {
  const [sexo, setSexo] = useState("");
  const [consultando, setConsultando] = useState(false);
  const [error, setError] = useState(null);
  const [resultado, setResultado] = useState(null);

  const consultar = async () => {
    setConsultando(true);
    setError(null);
    try {
      const data = await consultarMultasPersona(dni, sexo || null);
      setResultado(data);
    } catch (e) {
      setError(e?.message || "No se pudo consultar");
    } finally {
      setConsultando(false);
    }
  };

  const actas = resultado?.penalties || [];
  const avisos = (resultado?.warnings || []).filter(Boolean);

  return (
    <div>
      {puedeConsultar && dni && (
        <div className="pf-multas-cab">
          <div className="pf-chico">
            <label style={{ marginRight: 12 }}>
              <input
                type="radio"
                name={`sexo-${dni}`}
                checked={sexo === ""}
                onChange={() => setSexo("")}
              />{" "}
              Sólo CABA
            </label>
            <label style={{ marginRight: 12 }}>
              <input
                type="radio"
                name={`sexo-${dni}`}
                checked={sexo === "M"}
                onChange={() => setSexo("M")}
              />{" "}
              Varón (+ PBA/SINAI)
            </label>
            <label>
              <input
                type="radio"
                name={`sexo-${dni}`}
                checked={sexo === "F"}
                onChange={() => setSexo("F")}
              />{" "}
              Mujer (+ PBA/SINAI)
            </label>
          </div>
          <button
            type="button"
            className="pf-mover__btn"
            disabled={consultando}
            onClick={consultar}
            title="Consulta real contra CABA/PBA/SINAI. Puede tardar hasta 3 minutos."
          >
            {consultando ? "Consultando… (hasta 3 min)" : "Consultar"}
          </button>
        </div>
      )}

      {error && <p className="pf-nopude">{error}</p>}

      {avisos.length > 0 && <p className="pf-nopude">{avisos.join(" · ")}</p>}

      {resultado && (
        actas.length === 0 ? (
          <p className="tenue">No encontré multas para este DNI.</p>
        ) : (
          <>
            <p className="pf-dato">
              <b>{actas.length}</b> acta(s) encontrada(s)
            </p>
            <div className="tabla-wrap">
              <table className="tabla fija">
                <thead>
                  <tr>
                    <th>Jurisdicción</th>
                    <th>Dominio</th>
                    <th>Acta</th>
                    <th>Fecha</th>
                    <th>Descripción</th>
                    <th>Monto</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {actas.map((a, i) => (
                    <tr key={`${a.jurisdiction}-${a.act_number}-${i}`}>
                      <td>{a.jurisdiction}</td>
                      {/* Puede no venir: CABA no dice de qué vehículo es cada
                          acta. Cuando viene, como chapa (Igna, 03/10). */}
                      <td>{a.domain ? <Patente dominio={a.domain} /> : "—"}</td>
                      <td>{a.act_number}</td>
                      <td>{diaAr(a.occurred_at) || "—"}</td>
                      <td>{a.description || "—"}</td>
                      <td>{money(a.amount)}</td>
                      <td>{a.status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )
      )}
    </div>
  );
};

export default BloqueMultasPersona;
